import { useEffect, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "./ui.tsx";

/**
 * 「填个理由再干这件事」的对话框。
 *
 * 🔴 **后台原来有十处用 `window.prompt` 收理由** —— 驳回商品、停业整顿、
 *    转移客户、没收保证金、禁用账号…… 本项目明确要求这些危险操作**必须写理由**
 *    （纠纷时那是唯一依据），而浏览器原生弹窗：
 *    - 没有样式，看着像页面出了故障；
 *    - **不告诉他这一下会造成什么后果** —— 而这几件事分别是"钱扣回来"
 *      "商家不能做生意了""客户换人了"；
 *    - 不能校验、不能多行，空白理由照样能提交上去。
 *
 * ⚠️ 危险动作的按钮用 `danger`，并把后果写在描述里 —— 让人在按下去之前
 *    读到一句"这会怎样"，比事后再解释有用得多。
 */
export function ReasonDialog({
  open,
  title,
  description,
  placeholder = "写清楚原因，对方会原样看到",
  confirmLabel = "确认",
  danger,
  loading,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  /** 🔴 这一句要说清楚"按下去会发生什么"。 */
  description?: string;
  placeholder?: string;
  confirmLabel?: string;
  danger?: boolean;
  loading?: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");

  /** ⚠️ 每次打开都清空 —— 留着上一次的理由，很容易把上一单的话按到这一单上。 */
  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {/*
          ⚠️ 用 textarea 不用 input：理由经常要写两三句
          （"瓷砖有磕碰，已与买家确认退货"），单行框写到一半就看不见前面了。
        */}
        <textarea
          autoFocus
          rows={3}
          className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2.5 text-[14px] leading-relaxed outline-none focus:border-brand-500"
          placeholder={placeholder}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            取消
          </Button>
          <Button
            variant={danger ? "danger" : "primary"}
            loading={loading}
            /** ⚠️ 空理由不给过 —— 服务端也会拒，但让他点了才知道是最糟的。 */
            disabled={!reason.trim()}
            onClick={() => onConfirm(reason.trim())}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
